import { computed, type ComputedRef, type Ref } from 'vue'

import useProgram from '@/features/programs/hooks/use-program.hook'
import { selectGetSelectedProgramId } from '@/features/programs/stores/programs/programs.selectors'
import programsStore from '@/features/programs/stores/programs/programs.store'

/**
 * Hook centralisé pour gérer les settings de matching
 *
 * Expose deux propriétés principales :
 * - canFillCriteria: Les utilisateurs peuvent renseigner leurs critères de matching
 * - canAutoMatch: Les mentorés peuvent déclencher le matching automatique
 *
 * @param programIdRef - ID du programme (optionnel, utilise le programme sélectionné par défaut)
 * @returns { canFillCriteria, canAutoMatch, matchingCriteriaEnabled, matchingAutoSuggestEnabled }
 */
export default function useMatchingSettings(
  programIdRef?: Ref<string | undefined> | ComputedRef<string | null>
) {
  // Si aucun programId fourni, utiliser le programme sélectionné
  const fallbackProgramIdRef = selectGetSelectedProgramId(programsStore)

  // Wrapper dans un computed pour assurer le type ComputedRef<string | null>
  const selectedProgramIdRef = programIdRef
    ? computed(() => (programIdRef.value ?? null) as string | null)
    : fallbackProgramIdRef

  const { programRef } = useProgram(selectedProgramIdRef)

  /**
   * Option 1 : Activer les critères de matching
   * Permet aux mentors et mentorés de renseigner leurs critères
   */
  const matchingCriteriaEnabled = computed(
    () => programRef.value?.settings.matchingCriteriaEnabled === true
  )

  /**
   * Option 2 : Autoriser le matching automatique
   * Permet aux mentorés de déclencher la recherche automatique de mentor
   * Cette option dépend de l'option 1 (critères doivent être activés)
   */
  const matchingAutoSuggestEnabled = computed(
    () => programRef.value?.settings.matchingAutoSuggestEnabled === true
  )

  /**
   * Alias pour faciliter la migration depuis isMatchingEnabled
   * Les utilisateurs peuvent renseigner leurs critères de matching
   */
  const canFillCriteria = computed(() => matchingCriteriaEnabled.value)

  /**
   * Les mentorés peuvent déclencher le matching automatique
   * Nécessite que les critères soient également activés
   */
  const canAutoMatch = computed(() => {
    return matchingCriteriaEnabled.value && matchingAutoSuggestEnabled.value
  })

  return {
    // Propriétés brutes
    matchingCriteriaEnabled,
    matchingAutoSuggestEnabled,

    // Alias pratiques
    canFillCriteria,
    canAutoMatch,

    // Programme pour accès complet si besoin
    programRef,
  }
}
